/**
 * Transitory-origin migration (ENG-4486 Fix A.3).
 *
 * An auth-required camera is bulk-added as a FRONTEND-ONLY transitory
 * placeholder (`device/transitory.ts`, id `transitory:…`) and registered as a
 * multi-add origin so the post-add observer fires its "Authenticate" toast
 * (Fix A.1/A.2). When the user authenticates, the ONVIF path commits a REAL
 * device with a fresh id and drops the placeholder (`auth-dispatch.ts`). This
 * helper hands the origin off from the placeholder id to the real id so the
 * observer keeps firing for the REAL device (e.g. a still-missing model →
 * "Select Model", progressive resolution), and clears the now-stale
 * transitory "Authenticate" toast. This is NOT a §9.3 auto-clear violation —
 * authentication is genuinely resolved, so the auth alert is superseded.
 *
 * The core is PURE + fully dependency-injected (no depot / notifications
 * import) so every branch is unit-testable without a live depot — same
 * pattern as `dispatchAuthSubmit`. The `…ToReal` wrapper wires the real
 * primitives and is the single seam the React adapter
 * (`authentication-content.tsx`) calls.
 *
 * Per `think-in-systems` + `reference-rules-in-comments` (ai.knowledge/global).
 */
import { depot } from '#~/depot/index.js';
import { clear } from './utils.js';
import { registerMultiAddOrigin, unregisterMultiAddOrigin } from './multi-add-origins.js';
import { bucketToastId } from './hooks/multi-add-bucket-resolver.js';
import type { Device } from '@studio/models/device';

/** Injected seams for {@link migrateTransitoryOrigin}. */
export interface MigrateTransitoryOriginDeps {
  /** Placeholder (transitory) device id whose origin is being migrated. */
  placeholderId: string;
  /** Freshly-committed REAL device id the origin migrates to. */
  realId: string;
  /** Read the batchId of the placeholder's origin, or `undefined` if none. */
  readOriginBatchId: (id: string) => string | undefined;
  /** Register `[realId]` under the carried batchId. */
  registerOrigin: (ids: string[], batchId: string) => void;
  /** Remove a live notification by id (the stale transitory auth toast). */
  clearToast: (notificationId: string) => void;
  /** Drop the placeholder's origin entry. */
  unregisterOrigin: (id: string) => void;
}

/**
 * Migrate the multi-add origin from a transitory placeholder to its committed
 * real device (pure). No-op when the placeholder was never a bulk-add origin
 * (batchId absent) — e.g. a wizard/single-add auth, which must not touch the
 * origin slice.
 *
 * Order: register the real origin FIRST (so the observer can fire for it on
 * the next tick), then clear the stale transitory auth toast, then drop the
 * placeholder origin. The transitory alert is always a single `auth` fire
 * (`deriveMissingBuckets` on a placeholder → `['auth']`, never combined), so
 * only the `:auth:toast` id can exist — no `:combined:` clear is needed.
 */
export const migrateTransitoryOrigin = (deps: MigrateTransitoryOriginDeps): void => {
  const { placeholderId, realId, readOriginBatchId, registerOrigin, clearToast, unregisterOrigin } =
    deps;

  const batchId = readOriginBatchId(placeholderId);
  if (batchId === undefined) {
    // The placeholder was not a bulk-add origin (wizard / single-add, or an
    // already-migrated id) — nothing to migrate.
    return;
  }

  registerOrigin([realId], batchId);
  // Reuse the resolver's toast-id shape — the transitory fire is a single
  // `auth` toast, so its id is `${batchId}:${placeholderId}:auth:toast`.
  clearToast(bucketToastId(batchId, placeholderId, 'auth'));
  unregisterOrigin(placeholderId);
};

/**
 * Wired convenience wrapper the React adapter calls after an ONVIF
 * placeholder authenticates and commits (`{kind:'committed'}`). Threads the
 * real depot/notifications primitives into the pure core so the adapter gains
 * only this single import.
 */
export const migrateTransitoryOriginToReal = (
  placeholderId: Device['id'],
  realId: Device['id']
): void =>
  migrateTransitoryOrigin({
    placeholderId,
    realId,
    readOriginBatchId: (id) => depot.getState().multiAddOriginByDeviceId[id]?.batchId,
    registerOrigin: registerMultiAddOrigin,
    clearToast: clear,
    unregisterOrigin: unregisterMultiAddOrigin
  });
